import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { getLoadedSudo, useSudoStale } from '../../utils/sudoState';
import { clearCopilotConversation, readCopilotConversation, writeCopilotConversation } from './copilotStorage';
import { startSessionWarmup } from './copilotSession';

// ---------------------------------------------------------------------------
// Copilot conversation state, shared across the whole app.
//
// A conversation is bound to one case (the backend binds the session to it and
// rejects other cases with 409). It moves through three states:
//   - none:   nothing open; the trigger shows only on an eligible case page.
//   - draft:  panel opened on a case page, nothing sent yet. Leaving that case
//             discards it, so it never shows up on other pages.
//   - active: at least one message sent. It follows the user to every page,
//             keeps its open/closed state across navigation, and is persisted
//             per tab so a reload restores it (see copilotStorage).
// "New chat" is only possible on an eligible case page and replaces the
// current conversation; there is no way back to the previous one.
// ---------------------------------------------------------------------------

const CopilotContext = createContext(null);

// The backend deletes a session's sandbox 6 h after its last request (sliding
// window, copilot-next values.yaml `ttlSeconds: 21600`) and silently starts an
// empty one for the next request. The frontend makes every one of those
// requests, so it mirrors the clock and stops the user from talking to a
// sandbox that has forgotten everything. Ten minutes of slack covers clock
// skew between the browser and the cluster.
export const SESSION_TTL_MS = (6 * 60 - 10) * 60 * 1000;
const EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

function mintSessionId(userId, workspaceId) {
  return ['session', userId, workspaceId, Date.now()].filter(Boolean).join('_');
}

function isExpired(conversation) {
  return !!conversation?.started && Date.now() - conversation.lastRequestAt > SESSION_TTL_MS;
}

export function CopilotProvider({ children }) {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const userId = currentUser?.id;
  // The workspace requests actually go to. Under sudo that is the one in
  // localStorage, not the user's own that /users/me reports, and a session is
  // bound to it: a conversation from one workspace must never be restored in
  // another. String on both sides — the sudo id is stored as text.
  const ownWorkspaceId = currentUser?.workspace?.id;
  const workspaceId = ownWorkspaceId == null ? undefined : String(getLoadedSudo().id ?? ownWorkspaceId);
  const enabled = !!currentUser?.workspace?.config?.featureflags?.includes('copilot');
  // Another tab entered or left sudo: every request from this tab now carries
  // that workspace's header (see utils/sudoState.ts) while the page, and any
  // conversation, still belong to the one it loaded under. Switching in this
  // tab is a full reload, so the stored conversation is dropped by the
  // workspace check on hydration instead.
  const sudoStale = useSudoStale();

  // The case page currently mounted (registered via useCopilotPage), if any.
  const [page, setPage] = useState(null);
  const [conversation, setConversationState] = useState(null);
  const [isOpen, setIsOpenState] = useState(false);
  const [unread, setUnread] = useState(false);
  // Bumped whenever the runtime must start from scratch (new/discarded/hydrated
  // conversation): the chat component remounts its assistant-ui runtime on it.
  const [threadKey, setThreadKey] = useState(0);

  // Refs mirror state for callbacks that run from child effects, which fire
  // before this component's own effects would have synced a ref.
  const conversationRef = useRef(null);
  const isOpenRef = useRef(false);
  const threadRuntimeRef = useRef(null);
  // Session ids of drafts that were opened (and warmed up) but discarded when
  // the user left the case: coming back reuses the warm sandbox instead of
  // paying the ~20s cold start again. Only drafts live here — an active
  // conversation's id must never be reused for an empty chat.
  const draftSessionsRef = useRef(new Map());
  const hydratedRef = useRef(false);
  // Set right before we cancel a run ourselves (Stop, New chat, discard). A
  // run that ends as "cancelled" without it was aborted from outside — the
  // browser tearing the page down on reload — and must stay marked pending.
  const stopRequestedRef = useRef(false);
  // Mode ('qa' | 'report') of the most recent send; persisted with a pending
  // request so a resend goes out the same way.
  const lastTurnModeRef = useRef('qa');
  // True once the page is being unloaded: the snapshot written on `pagehide`
  // is final and must not be overwritten by the stream being torn down.
  const unloadingRef = useRef(false);

  const cancelRun = useCallback(() => {
    stopRequestedRef.current = true;
    threadRuntimeRef.current?.cancelRun();
  }, []);

  const snapshotFor = useCallback(
    (conv, messages, pending) => ({
      sessionId: conv.sessionId,
      userId: conv.userId,
      workspaceId: conv.workspaceId,
      narrativeId: conv.narrativeId,
      narrativeTitle: conv.narrativeTitle,
      narrativePath: conv.narrativePath,
      lastRequestAt: conv.lastRequestAt,
      messages,
      pending,
    }),
    [],
  );

  // Reloading (or closing) the tab mid-answer: the backend cancels the turn
  // when the stream drops, so record the request as pending right now, while
  // the page is still alive, instead of relying on how the browser happens to
  // end the fetch. `beforeunload` is the earliest point (nothing is cancelled
  // yet, so the run is still visibly in flight); `pagehide` is the backstop.
  // The app has no beforeunload prompt, so both always lead to an unload;
  // `pageshow` undoes the flag if the page comes back from the bfcache.
  useEffect(() => {
    const onUnload = () => {
      const conv = conversationRef.current;
      const runtime = threadRuntimeRef.current;
      if (conv?.started && runtime && !unloadingRef.current) {
        const state = runtime.getState();
        if (state.isRunning) {
          const messages = state.messages
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .map((m) => ({ role: m.role, content: m.content.find((p) => p.type === 'text')?.text ?? '' }));
          const lastUser = [...messages].reverse().find((m) => m.role === 'user');
          if (lastUser) {
            writeCopilotConversation(
              snapshotFor(conv, messages, { text: lastUser.content, mode: lastTurnModeRef.current }),
            );
          }
        }
      }
      unloadingRef.current = true;
    };
    const onPageShow = () => {
      unloadingRef.current = false;
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [snapshotFor]);

  const setConversation = useCallback((next) => {
    conversationRef.current = next;
    setConversationState(next);
  }, []);

  const setIsOpen = useCallback((next) => {
    isOpenRef.current = next;
    setIsOpenState(next);
  }, []);

  const resetThread = useCallback(() => setThreadKey((k) => k + 1), []);

  const discardConversation = useCallback(() => {
    cancelRun();
    clearCopilotConversation();
    setConversation(null);
    setIsOpen(false);
    setUnread(false);
    resetThread();
  }, [cancelRun, setConversation, setIsOpen, resetThread]);

  // Restore a persisted conversation once the user is known; drop it if it
  // belongs to another user or workspace (login as someone else, workspace
  // switch) or if the user goes away altogether (logout — the provider sits
  // above Layout and outlives it, so nothing may linger in memory or storage).
  // A `pending` turn means the tab reloaded mid-answer: the trailing request
  // (and any partial answer) is taken out of the history and surfaced as
  // "interrupted" so the user can send it again.
  useEffect(() => {
    if (!userId || !workspaceId) {
      if (conversationRef.current) discardConversation();
      return;
    }
    const current = conversationRef.current;
    if (current) {
      if (current.userId !== userId || current.workspaceId !== workspaceId) discardConversation();
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const stored = readCopilotConversation();
    if (!stored) return;
    if (stored.userId !== userId || stored.workspaceId !== workspaceId) {
      clearCopilotConversation();
      return;
    }
    let messages = stored.messages;
    let interrupted = null;
    if (stored.pending) {
      while (messages.length && messages[messages.length - 1].role === 'assistant') messages = messages.slice(0, -1);
      if (messages.length && messages[messages.length - 1].role === 'user') messages = messages.slice(0, -1);
      interrupted = stored.pending;
    } else if (messages.length && messages[messages.length - 1].role === 'user') {
      // A trailing question with no answer at all means the answer never
      // arrived; offer to resend rather than restore a dangling bubble.
      interrupted = { text: messages[messages.length - 1].content, mode: 'qa' };
      messages = messages.slice(0, -1);
    }
    setConversation({
      sessionId: stored.sessionId,
      userId,
      workspaceId,
      narrativeId: stored.narrativeId,
      narrativeTitle: stored.narrativeTitle,
      narrativePath: stored.narrativePath,
      lastRequestAt: stored.lastRequestAt ?? Date.now(),
      initialMessages: messages,
      started: true,
      interrupted,
    });
    resetThread();
  }, [userId, workspaceId, discardConversation, setConversation, resetThread]);

  // A conversation must not go on talking to another workspace: stop the run,
  // forget the session and close the panel. The copilot stays hidden until the
  // tab reloads into a workspace of its own and lands on an eligible case.
  useEffect(() => {
    if (sudoStale && conversationRef.current) discardConversation();
  }, [sudoStale, discardConversation]);

  // Expiry is a function of time, so re-evaluate it when the conversation
  // changes and once a minute while one exists (the panel may stay open
  // across the boundary).
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    setExpired(isExpired(conversation));
    if (!conversation?.started) return undefined;
    const timer = setInterval(() => setExpired(isExpired(conversationRef.current)), EXPIRY_CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [conversation]);

  // A draft only lives on the case it was opened for.
  useEffect(() => {
    const current = conversationRef.current;
    if (current && !current.started && current.narrativeId !== page?.narrativeId) {
      discardConversation();
    }
  }, [page, discardConversation]);

  const createDraft = useCallback(
    (forPage, { reuseWarmSession }) => {
      let sessionId = reuseWarmSession ? draftSessionsRef.current.get(forPage.narrativeId) : undefined;
      if (!sessionId) {
        sessionId = mintSessionId(userId, workspaceId);
        draftSessionsRef.current.set(forPage.narrativeId, sessionId);
        startSessionWarmup(forPage.narrativeId, sessionId);
      }
      setConversation({
        sessionId,
        userId,
        workspaceId,
        narrativeId: forPage.narrativeId,
        narrativeTitle: forPage.title,
        narrativePath: forPage.path,
        lastRequestAt: Date.now(), // the warm-up request counts on the backend too
        initialMessages: [],
        started: false,
        interrupted: null,
      });
      resetThread();
    },
    [userId, workspaceId, setConversation, resetThread],
  );

  const canStartNewChat = !!page?.eligible && !sudoStale;

  const open = useCallback(() => {
    if (!conversationRef.current) {
      if (!canStartNewChat) return;
      createDraft(page, { reuseWarmSession: true });
    }
    setUnread(false);
    setIsOpen(true);
  }, [page, canStartNewChat, createDraft, setIsOpen]);

  const close = useCallback(() => setIsOpen(false), [setIsOpen]);

  const startNewChat = useCallback(() => {
    if (!canStartNewChat) return;
    cancelRun();
    clearCopilotConversation();
    createDraft(page, { reuseWarmSession: false });
    setUnread(false);
    setIsOpen(true);
  }, [page, canStartNewChat, cancelRun, createDraft, setIsOpen]);

  const registerThreadRuntime = useCallback((runtime) => {
    threadRuntimeRef.current = runtime;
  }, []);

  // Snapshot of the live thread for analytics (the trigger button sits
  // outside the runtime provider and can't use the thread hooks).
  const getThreadSnapshot = useCallback(() => {
    const state = threadRuntimeRef.current?.getState();
    return { messagesCount: state?.messages.length ?? 0, isRunning: state?.isRunning ?? false };
  }, []);

  // Called by the chat whenever its message list or running state changes.
  // The first user message turns a draft into an active conversation; every
  // change after that is written through to storage. `cancelled` says the
  // run that just ended was aborted rather than completed.
  const reportActivity = useCallback(
    ({ messages, isRunning, cancelled }) => {
      if (unloadingRef.current) return;
      const current = conversationRef.current;
      if (!current) return;
      const mode = lastTurnModeRef.current;
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      if (!current.started && !lastUser) return;

      // An abort we did not ask for is the page being unloaded mid-answer:
      // keep the request pending so the reloaded tab offers to resend it.
      const interruptedByUnload = cancelled && !stopRequestedRef.current;
      // A new run clears any stale stop request (e.g. Stop pressed with nothing running).
      if (cancelled || isRunning) stopRequestedRef.current = false;

      let next = current;
      if (!next.started) {
        draftSessionsRef.current.delete(next.narrativeId);
        next = { ...next, started: true };
      }
      if (next.interrupted && messages.length > next.initialMessages.length) {
        next = { ...next, interrupted: null };
      }
      // A running turn means a request just went out: the backend's 6 h clock restarted.
      if (isRunning) next = { ...next, lastRequestAt: Date.now() };
      if (next !== current) setConversation(next);

      let pending = null;
      if ((isRunning || interruptedByUnload) && lastUser) pending = { text: lastUser.content, mode };
      else if (next.interrupted) pending = next.interrupted; // still unresolved after a reload
      writeCopilotConversation(snapshotFor(next, messages, pending));
    },
    [setConversation, snapshotFor],
  );

  const openRef = useRef(open);
  openRef.current = open;

  // An answer landed while the panel was closed (or the user was on another
  // page): mark it unread and say so, with the toast as a shortcut back in.
  const reportTurnFinished = useCallback(({ cancelled }) => {
    if (isOpenRef.current || cancelled) return;
    setUnread(true);
    const title = conversationRef.current?.narrativeTitle;
    toast.info(title ? t('Copilot has answered your question about {{case}}', { case: title }) : t('Copilot has answered your question'), {
      toastId: 'copilot-answer-ready',
      onClick: () => openRef.current(),
    });
  }, [t]);

  const status = !conversation ? 'none' : conversation.started ? 'active' : 'draft';
  const visible = enabled && !sudoStale && (!!page?.eligible || status === 'active');

  const value = useMemo(
    () => ({
      enabled,
      visible,
      status,
      page,
      conversation,
      isOpen,
      unread,
      threadKey,
      canStartNewChat,
      expired,
      open,
      close,
      startNewChat,
      cancelRun,
      lastTurnModeRef,
      registerThreadRuntime,
      getThreadSnapshot,
      reportActivity,
      reportTurnFinished,
      setPage,
    }),
    [
      enabled,
      visible,
      status,
      page,
      conversation,
      isOpen,
      unread,
      threadKey,
      canStartNewChat,
      expired,
      open,
      close,
      startNewChat,
      cancelRun,
      registerThreadRuntime,
      getThreadSnapshot,
      reportActivity,
      reportTurnFinished,
    ],
  );

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export function useCopilot() {
  return useContext(CopilotContext);
}

// Case pages call this to tell the copilot which case is on screen and whether
// it qualifies for a chat. Unregistering (unmount or switching case) is what
// closes a draft that was never sent.
export function useCopilotPage({ narrativeId, title, eligible }) {
  const copilot = useCopilot();
  const setPage = copilot?.setPage;
  const { pathname } = useLocation();

  useEffect(() => {
    if (!setPage || !narrativeId) return undefined;
    setPage({ narrativeId, title: title ?? '', path: pathname, eligible: !!eligible });
    return () => setPage(null);
  }, [setPage, narrativeId, title, pathname, eligible]);
}
